import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { ReviewModeProvider } from "./context/ReviewModeContext";
import Login from "./components/Login";
import AdminLayout from "./layouts/AdminLayout";

import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import SearchDocuments from "./pages/SearchDocuments";
/*
 * Search Documents v2 is temporarily hidden (do not delete).
 * import SearchDocumentsVersion2 from "./pages/SearchDocumentsVersion2";
 *
 * <Route
 *   path="/search-v2"
 *   element={
 *     <ProtectedRoute resource="SEARCH_DOC" access="READ">
 *       <SearchDocumentsVersion2 />
 *     </ProtectedRoute>
 *   }
 * />
 */
import ArchivedPolicySearch from "./pages/ArchivedPolicySearch";
import ArchivedClaimsSearch from "./pages/ArchivedClaimsSearch";
/*
 * Inbox Review is temporarily hidden from nav (do not delete).
 * import InboxReview from "./pages/InboxReview";
 */
import InboxReviewVersion2 from "./pages/InboxReviewVersion2";
import RequestPool, {
  HolidaysPage,
  ManualAssignmentPage,
  RequestLeavePage,
} from "./pages/RequestPool";
import Users from "./pages/Users";
import Roles from "./pages/Roles";
import Resources from "./pages/Resources";
import UserRoleAudit from "./pages/UserRoleAudit";
import AssignRoleResource from "./pages/AssignRoleResource";
import AssignUserRole from "./pages/AssignUserRole";
import AuditLogs from "./pages/AuditLogs";
import AccessReview from "./pages/AccessReview";
import LookupManagement from "./pages/LookupManagement";
import AdminDashboard from "./pages/AdminDashboard";
import Manage from "./pages/Manage";
import {
  SchedulerPage,
  RoleDocumentMappingPage,
  RoleWorkflowOrderPage,
} from "./pages/manageSubPages";


import ProtectedRoute from "./components/ProtectedRoute";
import Miscellaneous from "./pages/Miscellaneous";
import Department from "./pages/Department";
// Performance Activity is embedded in System Analytics (User Activity tab).
// import PerformanceActivityPage from "./pages/PerformanceActivity/PerformanceActivityPage";
import TrackingAnalytics from "./pages/TrackingAnalytics";

function App() {
  const isAuthenticated = useIsAuthenticated();

  return (
    <ReviewModeProvider>
      <BrowserRouter>
        <Routes>
          {!isAuthenticated ? (
            <Route path="*" element={<Login />} />
          ) : (
            <>

              <Route path="/" element={<AdminLayout />}>
                {/* USER DASHBOARD — open to all authenticated users (no-role sees contact-admin message) */}
                <Route index element={<Dashboard />} />
                <Route
                  path="/upload"
                  element={
                    <ProtectedRoute resource="UPLOAD_DOCUMENT" access="READ">
                      <Upload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <ProtectedRoute resource="SEARCH_DOC" access="READ">
                      <SearchDocuments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/policy-search"
                  element={
                    <ProtectedRoute resource="ARCHIVAL_POLICY_SEARCH" access="READ">
                      <ArchivedPolicySearch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/claim-search"
                  element={
                    <ProtectedRoute resource="ARCHIVAL_CLAIM_SEARCH" access="READ">
                      <ArchivedClaimsSearch />
                    </ProtectedRoute>
                  }
                />
                {/*
                 * Inbox Review is temporarily hidden (do not delete).
                 * <Route
                 *   path="/document-hub"
                 *   element={
                 *     <ProtectedRoute resource="DOC_INDEX_HUB" access="READ">
                 *       <InboxReview />
                 *     </ProtectedRoute>
                 *   }
                 * />
                 */}
                <Route
                  path="/document-hub-v2"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <InboxReviewVersion2 />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/request-pool"
                  element={
                    <ProtectedRoute resource="REQUEST_POOL" access="READ">
                      <RequestPool />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manual-assignment"
                  element={
                    <ProtectedRoute resource="MANUAL_ASSIGNMENT" access="READ">
                      <ManualAssignmentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/timeoff"
                  element={
                    <ProtectedRoute resource="REQUEST_LEAVE" access="READ">
                      <RequestLeavePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/request-leave"
                  element={<Navigate to="/timeoff" replace />}
                />
                <Route
                  path="/time-off"
                  element={<Navigate to="/timeoff" replace />}
                />
                <Route
                  path="/holidays"
                  element={
                    <ProtectedRoute resource="HOLIDAYS" access="READ">
                      <HolidaysPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute resource="ANALYTICS" access="READ">
                      <TrackingAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-role-audit"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <UserRoleAudit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lookups"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <LookupManagement />
                    </ProtectedRoute>
                  }
                />

                {/* ADMIN DASHBOARD */}
                <Route
                  path="admin-dashboard"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="manage"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <Manage />
                    </ProtectedRoute>
                  }
                />
                {/* USERS */}
                <Route
                  path="users"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <Users />
                    </ProtectedRoute>
                  }
                />

                {/* ROLES */}
                <Route
                  path="roles"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <Roles />
                    </ProtectedRoute>
                  }
                />

                {/* RESOURCES */}
                <Route
                  path="resources"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <Resources />
                    </ProtectedRoute>
                  }
                />

                {/* DEPARTMENTS */}
                <Route
                  path="departments"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <Department />
                    </ProtectedRoute>
                  }
                />

                {/* USER ↔ ROLE */}
                <Route
                  path="assign-user-role"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <AssignUserRole />
                    </ProtectedRoute>
                  }
                />

                {/* ROLE ↔ RESOURCE */}
                <Route
                  path="assign-role-resource"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <AssignRoleResource />
                    </ProtectedRoute>
                  }
                />

                {/* AUDIT LOGS */}
                <Route
                  path="audit-logs"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <AuditLogs />
                    </ProtectedRoute>
                  }
                />

                {/* ACCESS REVIEW  */}
                <Route
                  path="access-review"
                  element={
                    <ProtectedRoute resource="ADMIN" access="READ">
                      <AccessReview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="scheduler"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <SchedulerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="role-document-mapping"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <RoleDocumentMappingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="manage-workflows"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <RoleWorkflowOrderPage />
                    </ProtectedRoute>
                  }
                />
                {/* MISCELLANEOUS */}
                <Route
                  path="miscellaneous"
                  element={
                    <ProtectedRoute resource="ADMIN" access="WRITE">
                      <Miscellaneous />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="assignments"
                  element={<Navigate to="/miscellaneous" replace />}
                />
                {/*
                 * Performance Activity is embedded in System Analytics (User Activity tab).
                 * <Route
                 *   path="performance-activity"
                 *   element={
                 *     <ProtectedRoute resource="ADMIN" access="READ">
                 *       <PerformanceActivityPage />
                 *     </ProtectedRoute>
                 *   }
                 * />
                 */}
              </Route>

            </>
          )}
        </Routes>
      </BrowserRouter>
    </ReviewModeProvider>
  );
}

export default App;
